import { FC, useEffect, Dispatch, SetStateAction } from 'react'
import cn from 'clsx'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
import uniqid from 'uniqid'
import 'swiper/css'
import CircleArrowLeft from '../../icons/CircleArrowLeft'
import Card from './Card'
import useAddItem from '@framework/cart/use-add-item'
import dataLayer from '@utils/dataLayer'

const ns = `cart-recommended-products`

interface Props {
  title?: string
  products: any
  data: any
  filteredProducts: Array<any>
  setFilteredProducts: Dispatch<SetStateAction<any>>
}

const CartRecommendedProducts: FC<Props> = ({
  title,
  products,
  data,
  filteredProducts,
  setFilteredProducts,
}) => {
  const rootClassNames = cn({
    [`${ns}`]: true,
  })

  const addItem = useAddItem()

  /**
   * Adds the selected product from the recommendedItems carousel to the cart.
   *
   * @param product | The selected product from the Card component
   * @returns void
   */
  const addToCart = async (product: any) => {
    if (!product) return null

    try {
      await addItem({
        productId: String(product?.id),
        variantId: String(product?.variants?.[0]?.id),
      })

      // Add event to data layer
      dataLayer().push(`add_to_cart`, [
        {
          product,
          variant: product?.variants?.[0],
        },
      ])
    } catch (err) {
      console.log(err) // eslint-disable
    }
  }

  useEffect(() => {
    setFilteredProducts([
      ...products?.filter((product: any) => {
        return !data?.lineItems.some((lineItem: any) => {
          return product?.id === lineItem?.productId
        })
      }),
    ])
  }, [data, products, setFilteredProducts])

  if (filteredProducts?.length === 0) return null

  return (
    <section className={rootClassNames}>
      <div className={`container-fluid ${ns}__container`}>
        {title && <span className={`${ns}__title`}>{title}</span>}
        <div className={`${ns}__items`}>
          <Swiper
            className={`${ns}__slider`}
            slidesPerView="auto"
            spaceBetween={20}
            freeMode
            modules={[Navigation]}
            navigation={{
              nextEl: `.swiper-button-next-collection`,
              prevEl: `.swiper-button-prev-collection`,
              disabledClass: 'swiper-button-disabled-collection',
            }}
          >
            {filteredProducts.map((product) => (
              <SwiperSlide
                key={`recommended-product-${uniqid()}`}
                className={`${ns}__slide`}
              >
                <Card product={product} addToCart={addToCart} />
              </SwiperSlide>
            ))}
          </Swiper>
          <div className={`${ns}__navigation`}>
            <button
              className={`swiper-button-prev-collection swiper-button-disabled-collection`}
            >
              <CircleArrowLeft width="60" height="60" />
              <span className={'visually-hidden'}>Previous</span>
            </button>
            <button className={`swiper-button-next-collection`}>
              <CircleArrowLeft width="60" height="60" />
              <span className={'visually-hidden'}>next</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CartRecommendedProducts
